/**
 * enemyAdapter.ts — Bridges the new enemy/wave system to the engine's EnemiesModule interface.
 * This adapter creates and manages EnemySystemState + WaveSystemState internally,
 * and exposes them through the registerEnemies() pattern the engine already uses.
 */

import type { GameState, Enemy, ViewBounds, EnemiesModule, PlayerId } from './types';
import { canMoveTo } from './types';
import {
  type EnemySystemState,
  type PlayerForEnemy,
  type CameraForEnemy,
  createEnemySystemState,
  updateEnemies,
  resetPlayerVelocityTracker,
  renderEnemies,
  getAliveEnemies,
  getAliveCount,
  damageEnemy as damageEnemyFn,
  cleanupDead,
  clearEnemyProjectiles,
  spawnEnemy,
  setEnemyProjectileSpeedMultiplier,
  setCurrentFloorForScaling,    // BETA 19.98 — push floor # so getScaledStats scales HP/damage per floor
  setFloorStartForScaling,      // BETA 20.77 — push floor start so endless overdrive runs floor-local
  setEndlessCcResist,           // BETA 21.31 — endless CC resistance so the swarm closes in late-game
} from './enemySystem';

// BETA 20.01 — Per-frame alive-enemy cache (see getEnemies() below).
let _aliveEnemiesCache: Enemy[] = [];
let _aliveEnemiesCacheFrame = -1;
let _frameCounter = 0;
import {
  type WaveSystemState,
  createWaveSystemState,
  updateWaveSystem,
  getFloorTimeRemaining,
} from './waveSystem';
import type { EnemyInstance } from './enemies';
import { getEnemyDef } from './enemies'; // BETA 20.86 — real-killer name lookup for deathCause
import { playPlayerHit, playEnemyHit, playDiarrheaGas, playLastStandActivate, playShieldBreak, playMorningWoodThorns } from './gameSfx';
import { spawnDrops } from './lootOrbs';
import { getLastFacingAngle } from './weaponAdapter';
import { resetSillyJuiceOnDamage, getItemState, setActiveItemsAdapterPlayer } from './itemsAdapter';
import { tuneOf } from './bunchaBullshiiii'; // BETA 21.79 — editable Timmy Repellent / Adrenaline magnitudes
// BETA 15.26 — character passive damage-taken hook (Sponge Absorb stack +,
// Silly Juice character reset)
import { onPlayerTookDamage, setActiveCharacterPassivePlayer } from './characterPassives';
import {
  spawnPoisonCloud,
  spawnBubbleWrapReflect,
  spawnMorningWoodExplosion,
  spawnWyfeBeaterThorns,
  triggerBubbleWrapInvuln,
  drainAoeDamageRequests,
  getBubbleWrapInvulnTimer,
  setItemEffectLocalPlayerId,   // BETA 23.6x — CO-OP PHASE 2 part C (§3.3) — AoE owner stamp source
} from './itemEffectSystem';
// BETA 14.58 — perc proc trackers wired at the damage callback for
// N13 (Not Xanax: post-damage timer fires) and N14 (Not Acid: phase trigger).
import { trackPercProc, trackItemProc, trackItemDmg, trackSynergyTrigger, trackFloorEffectSpawn, peekLastDamageSource, setLastDamageSource } from './weaponLab';
// BETA 16.22 — gas damage overlay + floating tick text.
import { isGasSource, notifyEnemyGasTick } from './gasOverlay';
// BETA 16.31 — non-gas AoE hits (R09 Big Brain bursts, R08 rocket etc.)
// need to spawn floating damage numbers via the weaponSystem pipeline.
import { getWeaponSystemState } from './weaponAdapter';
import { spawnDamageNumber } from './weaponSystem';
import { debugEnemyDamageMult } from './debugState'; // BETA 20.91 — live "Enemy DMG mult" knob
import { rollRng } from './rng'; // BETA 20.94 — seeded balance rolls (item-effect procs)

// ─── BETA 20.86 — resolve the REAL killer from the last-damage-source string ──
// The source set right before each player.damage() is ground truth for what
// dealt THIS hit: a bare enemy definitionId ('zombie'), a slam ('boss_slam'),
// or a throw ('<enemy>_throw' as of 20.86). Parse it back to a named enemy so the
// death screen + telemetry credit the ACTUAL source instead of whatever body was
// nearest. Returns null for sources we can't name (legacy generic projectile,
// non-enemy) so the caller can fall back to the nearest-enemy heuristic.
export function _resolveKillerFromSource(
  src: string | null,
  state: { enemies?: Array<{ alive?: boolean; definitionId?: string; variant?: string }> },
): { type: string; variant: string; name: string } | null {
  if (!src) return null;
  let base = src;
  let kind: 'contact' | 'slam' | 'throw' = 'contact';
  if (base.endsWith('_slam')) { base = base.slice(0, -5); kind = 'slam'; }
  else if (base.endsWith('_throw')) { base = base.slice(0, -6); kind = 'throw'; }
  // Generic / non-enemy sources we can't name — let the caller use nearest.
  if (base === 'enemy_projectile' || base === 'boss_projectile' || base === 'enemy' || base === 'boss') {
    return null;
  }
  const def = getEnemyDef(base);
  if (!def) return null; // not a known enemy id → fall back to nearest
  // Best-effort variant: a live enemy of this def (the thrower may already be
  // dead/off-screen for a throw, so default to 'normal' when none is found).
  let variant = 'normal';
  for (const e of state.enemies ?? []) {
    if (e.alive && e.definitionId === base) { variant = (e.variant as string) ?? 'normal'; break; }
  }
  const nice = def.name ?? (base.charAt(0).toUpperCase() + base.slice(1));
  const pretty =
    variant === 'elite' ? `Crowned ${nice}`
    : variant === 'boss' ? `Boss ${nice}`
    : nice;
  const name =
    kind === 'slam' ? `${pretty} (slam)`
    : kind === 'throw' ? `${pretty} (throw)`
    : pretty;
  return { type: base, variant, name };
}

// BETA 20.87 — POST-HIT INVINCIBILITY (the genre standard: Vampire Survivors /
// Brotato / Halls of Torment / Megabonk all do this). Contact i-frames were
// REMOVED in BETA 14.30 for "swarm pressure" — but that's exactly what let a
// full-HP player get deleted in a single frame (Dylan's run a7c89e26: 104→0 with
// ~10 enemies landing the same tick). After ANY enemy hit lands, the player is
// briefly invulnerable, so a swarm grinds you down over a couple seconds — with
// time to SEE it and react — instead of erasing you instantly. Damage still
// scales with time/level; nothing is capped. This is the single tunable dial:
// bigger = more forgiving, smaller = more frantic. ~0.4s ≈ 2.5 hits/sec ceiling.
// BETA 21.12 — Dylan: "getting demolished from things nowhere near me. Maybe a
// 0.5 second immunity after getting whacked would be nice." Bumped 0.4 → 0.5
// (= 2 hits/sec ceiling) so a stream of off-screen throwables can't chunk you
// down before you can react. Applies to projectiles + contact alike (line ~452).
// (BETA 23.6x §6 — dead `CONTACT_HIT_IFRAME` const deleted: the 21.27
// per-enemy-cooldown contact model made it unused; nothing read it.)

// BETA 21.27 — MEGABONK CONTACT MODEL (Dylan: "in megabonk can more than one enemy
// attack you at once?" — yes, and ours is the odd one out). 20.87 re-added a GLOBAL
// post-hit i-frame: one enemy tap → immune to the WHOLE swarm for 0.5s, so 200 enemies
// (or sitting inside a mega boss) did the damage of ONE enemy. That UNDID the 14.29
// per-enemy-cooldown swarm model and is the real reason you could camp a swarm/boss.
// Reverted: each enemy hits on its OWN per-enemy contactCooldown (set in enemySystem.ts),
// so MORE enemies on you = MORE damage (21.25's ENDLESS i-frame decay is REPLACED by
// this — it's a band-aid on a model we just removed). Safety so a wall can't one-frame
// a full-HP player (the legit thing 20.87 guarded): total contact HP loss is capped to
// CONTACT_WINDOW_CAP_FRAC of MAX HP per CONTACT_WINDOW_SEC. Hit the cap → a short i-frame
// blocks the rest of that window → you get ~1s of "GET OUT" before the swarm finishes
// you. Single/few enemies never reach the cap, so positioned play is unchanged.
const CONTACT_WINDOW_SEC = 0.5;          // rolling window for the swarm-damage cap
const CONTACT_WINDOW_CAP_FRAC = 0.5;     // max HP loss per window when fully surrounded (≈ death in ~1s)
const MIN_CONTACT_IFRAME = 0.05;         // tiny same-frame guard so ONE enemy can't double-process a frame

// ─── Internal State ─────────────────────────────────────────────────
let enemyState: EnemySystemState | null = null;
let waveState: WaveSystemState | null = null;
let canvasWidth = 1280;
let canvasHeight = 720;
let lastCleanupTime = 0;
let lastGameState: GameState | null = null; // stored each frame for spawnBossAt

// ─── Helpers: map engine types → enemy system types ─────────────────

/**
 * Build the PlayerForEnemy interface.
 * CRITICAL FIX: We pass a LIVE reference to the engine player object,
 * and the damage() callback mutates it directly. The iFrameTimer getter
 * reads the live value so multiple enemies in the same frame can't
 * double-hit the player.
 */
// BETA 23.6x — CO-OP PHASE 2 part A: parameterized by roster slot. Every
// write-capable field stays a LIVE proxy onto THAT player's real object (a
// snapshot for any write field resurrects the 22.02 knockback bug, now ×N
// players — this is law, PLAN §1a). damageImpl is the player's incoming-
// damage reducer; the returned view's damage() brackets it with the
// per-player store switches so every item/passive read inside resolves
// against the RIGHT player. Single-player: p1 → p1, all no-ops.
function toPlayerForEnemy(state: GameState, playerId: PlayerId): PlayerForEnemy {
  const p = state.players[playerId]!;
  const damageImpl = (amount: number, iFrameGrant: number, source?: string) => {
    // CO-OP part B (§5) — the attack source now rides the call: re-stamp the
    // module global just-in-time so every peekLastDamageSource() below reads
    // THIS hit's source even when two enemies hit two players in one frame.
    if (source) setLastDamageSource(source);
      // BETA 14.21 — count every attempted enemy → player attack regardless of
      // outcome. Bot CSV reads this + the 4 outcome counters (hit / iframe /
      // phase / evasion) so the analyst pass can compute hit-rate, dodge-rate,
      // iframe-block-rate, and damage-per-hit per build. User: "Enemies are
      // not actually hitting me when they should be."
      // BETA 20.04 — increment MOVED to enemySystem.ts:1379 (pre-cooldown gate).
      // Was here in the damage callback which only fired post-cooldown → 90-99%
      // of touch attempts were suppressed (e.g. 4 attempts logged with 178
      // alive enemies). Leaving this line removed so we don't double-count.

      // BETA 20.67 — deep-dive HIGH #5a: slam + projectile attacks never
      // counted as 'attempted' (only contact swings at enemySystem:1475),
      // while their outcomes DID land in hit/missed buckets → attempted <
      // hit + missed on boss/ranged-heavy runs. Count them here at the single
      // damage entry, keyed off the source set right before each call.
      {
        const _srcAtt = peekLastDamageSource();
        // BETA 21.42 — also count boss THROWS (<enemy>_throw). Their
        // outcomes landed in hit/missed buckets but were never counted as
        // 'attempted', so attempted < hit + missed on boss-heavy runs.
        if (_srcAtt && (_srcAtt.endsWith('_slam') || _srcAtt.includes('projectile') || _srcAtt.endsWith('_throw'))) {
          const _sAtt = state as unknown as Record<string, number | undefined>;
          _sAtt._enemyAttacksAttemptedThisRun = (_sAtt._enemyAttacksAttemptedThisRun ?? 0) + 1;
        }
      }
      // BETA 22.90 — N10 Debt damage-redirect-to-gold DELETED (Dylan: "delete
      // the old gold stuff"; N10 is HEART THROB now — proximity knockback,
      // engine.ts). The attack ledger simplifies back to attempted = hit +
      // missed; the _debt* / absorbedByDebt CSV fields stay defined but 0.

      if (p.invincibleTimer <= 0) {
        // BETA 19.94 — Reverted 19.91 combined dodge cap. Dylan: "we dont
        // cap things. we fix the balancing." Instead, lower the underlying
        // VALUES (N14 phaseChance bonusPerLevel + evasion shrines/items)
        // so building both to max takes more picks and reaches lower
        // ceilings. Independent rolls keep their natural math.
        // BETA 14.04 — Not Acid (perc N14): roll contactPhaseChance from
        // accumulated picks. If success, phase through this contact event
        // entirely (no damage, no i-frames, no flash).
        if ((p.contactPhaseChance ?? 0) > 0 && Math.random() < p.contactPhaseChance!) {
          // BETA 14.21 — outcome attribution
          state._enemyAttacksMissedByPhase = (state._enemyAttacksMissedByPhase ?? 0) + 1;
          // BETA 14.30 Phase 1 — phase-event counter
          state._phaseEventsThisRun = (state._phaseEventsThisRun ?? 0) + 1;
          // BETA 14.58 — N14 Not Acid procCount tracker
          trackPercProc('N14');
          // Brief minimum i-frame so the same enemy doesn't keep re-rolling 60x/sec
          p.invincibleTimer = Math.max(p.invincibleTimer, 0.15);
          return;
        }
        // Evasion: chance to dodge. Individual 80% cap (was always there).
        if ((p.evasion ?? 0) > 0 && Math.random() * 100 < Math.min(80, p.evasion)) {
          // BETA 14.21 — outcome attribution
          state._enemyAttacksMissedByEvasion = (state._enemyAttacksMissedByEvasion ?? 0) + 1;
          // BETA 14.30 Phase 1 — separate dodge-event counter (player POV)
          state._dodgeEventsThisRun = (state._dodgeEventsThisRun ?? 0) + 1;
          return;
        }
        // BETA 16.90 — Timmy Repellent Spray apply path (PSBOT cycle-30l §3
        // flagged this item: Dylan equipped it, died to Timmy, item was
        // never wired). The item def has effectId: 'timmy_repellent' but
        // there was no implementation anywhere. Now: read the last
        // damage source (set by enemySystem.ts:1332 on every contact hit)
        // and if it's 'timmy' AND the player has the item equipped,
        // halve the incoming damage. Speed-slow on nearby Timmys is a
        // separate effect (TODO if Dylan asks).
        // BETA 20.91 — live difficulty knob: the debug panel's "Enemy DMG mult"
        // scales ALL enemy→player damage from this one chokepoint. Defaults to
        // 1.0 (no change) for real players; only a dev slider moves it.
        let dmgIn = amount * (debugEnemyDamageMult !== 1 ? debugEnemyDamageMult : 1);
        const _lastSrc = peekLastDamageSource();
        // 23.55 — Timmy deleted; Bully Spray's -50% now keys off the 'brawler'
        // TAG (its card copy always said "Brawlers"). Damage sources arrive as
        // '<defId>' or '<defId>_throw'/'_slam' — strip the suffix to look up.
        const _srcDef = getEnemyDef((_lastSrc ?? '').replace(/_(throw|slam)$/, ''));
        if (_srcDef?.tags?.includes('brawler')) {
          const _items = getItemState();
          const _hasTimmyRepel = _items?.inventory.some(s => s.item.id === 'item_timmy_repellent_spray') === true;
          if (_hasTimmyRepel) {
            // BETA 17.07 — PSBOT cycle-30t Tier C counter #5: Timmy DR sum.
            const _sAnyTimmy = state as unknown as Record<string, number | undefined>;
            const _timmyDR = tuneOf('item_timmy_repellent_spray', 'damageReductionPct', 0.5); // BETA 21.79 — editable
            _sAnyTimmy._timmyDamageReducedThisRun = (_sAnyTimmy._timmyDamageReducedThisRun ?? 0) + (dmgIn * _timmyDR);
            dmgIn = dmgIn * (1 - _timmyDR);
            // Track proc for telemetry parity (item-trigger column in CSV).
            trackItemProc('item_timmy_repellent_spray');
          }
        }
        // BETA 17.19 — PSBOT cycle-30ah: shrine-channel damage reduction.
        // Long run took 13.5 dmg/sec (target 4-6) primarily from bot
        // standing still during shrine channels. Apply 40% damage
        // reduction whenever player is actively channeling a shrine.
        // BETA 17.62 — CRITICAL FIX: state.interactTarget is NEVER set to
        // 'shrine' anywhere in the engine — only 'boss_shrine'. So the
        // regular-shrine branch of this 17.19 damage reduction has been
        // dead code since the day it shipped. Now reads shrine state
        // directly (same fix as autoPlaytester's isChannelingShrine).
        const _isChanneling = state.interactTarget === 'boss_shrine' ||
          (state.map.roomMap.shrines ?? []).some(s => (s as { state?: string }).state === 'channeling');
        if (_isChanneling) {
          dmgIn = dmgIn * 0.6;
        }
        // Armor: flat damage reduction
        let reduced = Math.max(1, dmgIn - (p.armor ?? 0));
        // BETA 16.96 — Wire `damageReduction` % stat (PSBOT cycle-30e
        // Phase 3 audit gap). Items C06 / UC06 / R06 / R12 have
        // damageReduction modifiers that bunchaBullshiiiiSystem.ts
        // accumulates into stats.damageReduction (capped 80%) but
        // NOTHING was reading the field — silent no-op for years.
        // Now: applies as a percent reduction AFTER armor, so a build
        // with Underwear (12 armor + 6% DR) actually feels meaningful.
        const _itemsForDR = getItemState();
        const _drPct = _itemsForDR?.playerStats?.damageReduction ?? 0;
        if (_drPct > 0) {
          reduced = Math.max(1, reduced * (1 - _drPct / 100));
        }

        // Shield absorbs damage before HP
        // BETA 19.5 — Defensive: clamp NaN/undefined shield to 0 so the
        // absorption check doesn't silently skip. Was producing the
        // "died with full blue bar" bug — NaN > 0 is false → absorption
        // bypassed → damage hit HP directly while the bar still displays
        // maxShield.
        if (!Number.isFinite(p.shield)) p.shield = 0;
        if ((p.shield ?? 0) > 0) {
          // BETA — FULL BLOCK (Dylan: "i dont take any damage if i have shield...
          // and if the hit is strong enough it breaks the shield"). While the
          // shield is up, ANY hit deals ZERO HP damage. The hit drains the
          // shield by its full amount; a hit big enough to empty it BREAKS the
          // shield (then a longer cooldown before it regens). Nothing ever
          // overflows onto HP anymore (old behavior: overflow spilled to HP).
          const SHIELD_BREAK_COOLDOWN_SEC = 6.0; // "wait for the cooldown" on a break (upgrades shorten this)
          const _hit = reduced;
          const _prevShield = p.shield;
          const _broke = _hit >= p.shield;
          p.shield = Math.max(0, p.shield - _hit);
          reduced = 0; // fully blocked — nothing reaches HP
          const sAny = state as unknown as Record<string, number | undefined>;
          sAny._shieldDamageAbsorbedThisRun = (sAny._shieldDamageAbsorbedThisRun ?? 0) + Math.min(_prevShield, _hit);
          if (_broke) {
            p.shield = 0;
            // BREAK — shatter sound + longer cooldown before regen begins.
            sAny._shieldRegenDelayUntil = state.time + SHIELD_BREAK_COOLDOWN_SEC;
            sAny._shieldBrokeAtTime = state.time;
            playShieldBreak();
            state.screenShakeTimer     = 0.22;
            state.screenShakeIntensity = 9;
          } else {
            // Chip — blocked, shield still up. Short delay before it tops up.
            sAny._shieldRegenDelayUntil = state.time + 3.5;
            state.screenShakeTimer     = 0.10;
            state.screenShakeIntensity = 3;
          }
          // brief i-frames so a swarm can't chew the whole shield in one frame
          p.invincibleTimer = Math.max(p.invincibleTimer, iFrameGrant * 0.4);
          playEnemyHit(p.x, p.y);
          // No HP lost → counts as "missed by other" (not hit/phase/iframe/evasion).
          state._enemyAttacksMissedByOther = (state._enemyAttacksMissedByOther ?? 0) + 1;
          // Shield-absorbed hit marker (amount the shield ate).
          sAny._playerHitFlashTimer    = 0.55;
          sAny._playerHitLastDamage    = Math.min(_prevShield, _hit);
          sAny._playerHitLastWasShield = 1;
          return; // shield blocked the entire hit
        }

        // BETA 21.11 — PER-HIT DAMAGE CAP (anti one-shot). Dylan: "I get one
        // shot every game by a random flying boss projectile." Diagnosis: a
        // regular heavy's thrown zombie deals round(enemy.damage * 1.3) =
        // ~181 at his 240s spawn (enemySystem.ts:3252), vs a fresh 100-HP
        // LeBron — and NOTHING in this apply path capped a single hit. Every
        // past "fix" nerfed the heavy's CONTACT/slam, never the throw. The cap:
        // no single enemy hit (projectile / slam / contact) may remove more
        // than PER_HIT_MAXHP_FRAC of MAX HP. Applied AFTER armor/shield so
        // mitigation still helps; it's purely a ceiling on lethal spikes, and
        // because it's a fraction of maxHp it scales with HP builds and never
        // makes a hit feel free. `reduced` is also what the floating damage
        // number reads below, so the player sees the real (capped) number.
        // BETA 21.46 — in GOD MODE (post-final-boss endless) the anti-one-shot
        // caps are LIFTED on purpose. Dylan: this mode must KILL you fast — the
        // escalated swarm one-shotting you is the intended end (no infinite farm).
        // Normal play keeps the caps (unfair spikes there are still bad).
        const _godActive = ((state as unknown as Record<string, number | undefined>)._godMode ?? 0) > 0;
        const PER_HIT_MAXHP_FRAC = 0.40;
        const _perHitCap = Math.max(1, Math.round(p.maxHp * PER_HIT_MAXHP_FRAC));
        if (!_godActive && reduced > _perHitCap) reduced = _perHitCap;

        const projectedHp = p.hp - reduced;

        // BETA 13.68 / 20.86: capture the killer for the death screen + telemetry.
        // Dylan: "i died but couldnt tell what from... possible invisible enemy?
        // will it say if i die from a throwable from a mini-boss... i feel
        // like its wrong on what killed me most of the time."
        //
        // THE OLD BUG: this only ever tagged the NEAREST alive enemy at the fatal
        // frame — so a mini-boss throwable that hit from off-screen (or a slam)
        // got logged as "enemy:Zombie" simply because a zombie was closest. The
        // TRUE source of THIS hit is already on hand: setLastDamageSource() is
        // called right before every player.damage() (contact → 'zombie', slam →
        // 'boss_slam', throw → '<enemy>_throw' as of 20.86). Read it first; only
        // fall back to nearest-enemy when the source can't be resolved to a real
        // enemy (legacy generic projectile, or a non-enemy source).
        if (projectedHp <= 0) {
          const _resolved = _resolveKillerFromSource(peekLastDamageSource(), state);
          if (_resolved) {
            state.killedByType = _resolved.type;
            state.killedByVariant = _resolved.variant;
            state.killedByName = _resolved.name;
          } else if ((state.enemies?.length ?? 0) > 0) {
            // Fallback: nearest alive enemy (only when the real source is unknown).
            let nearestE: typeof state.enemies[number] | null = null;
            let nearestD = Infinity;
            for (const e of state.enemies) {
              if (!e.alive) continue;
              const d = Math.hypot(e.x - p.x, e.y - p.y);
              if (d < nearestD) { nearestD = d; nearestE = e; }
            }
            if (nearestE) {
              state.killedByType = nearestE.type;
              const variant = (nearestE as { variant?: string }).variant ?? 'normal';
              state.killedByVariant = variant;
              const niceType = nearestE.type.charAt(0).toUpperCase() + nearestE.type.slice(1);
              state.killedByName =
                variant === 'elite' ? `Crowned ${niceType}`
                : variant === 'boss' ? `Boss ${niceType}`
                : niceType;
            }
          }
        }

        // ── VooGoo Rager — CLUTCH SAVE: intercept a would-be-lethal hit ──
        // Dylan: "it should activate as soon as i hit the percentage... cause it
        // doesnt, ill just die." The per-frame poll only heals while you're ALIVE
        // and below 50%, so a hit that jumps you from above the threshold straight
        // to dead skipped it entirely. Now: when a hit WOULD kill you and VooGoo is
        // ready, it fires FIRST — you survive the hit and heal — instead of dying.
        // Runs before Last Stand so the free pity-save stays in reserve. Cooldown
        // lives on state so this and the per-frame poll share ONE timer.
        if (projectedHp <= 0) {
          const _vsa = state as unknown as Record<string, number | undefined>;
          const _voogooReady = (_vsa._voogooHealCd ?? 0) <= 0
            && (getItemState()?.inventory.some(s => s.item.id === 'item_voogoo_rager') ?? false);
          if (_voogooReady) {
            const _healAmt = Math.max(1, Math.round(p.maxHp * tuneOf('item_voogoo_rager', 'healPctOfMaxHp', 0.4)));
            p.hp = _healAmt;                                  // survive the lethal hit at the heal value
            _vsa._voogooHealCd = tuneOf('item_voogoo_rager', 'cooldownSeconds', 45);
            p.invincibleTimer = Math.max(p.invincibleTimer, 1.0); // brief escape window
            state._enemyAttacksHitThisRun = (state._enemyAttacksHitThisRun ?? 0) + 1;
            state._healEventsThisRun = (state._healEventsThisRun ?? 0) + 1;
            _vsa._healingReceivedThisRun      = (_vsa._healingReceivedThisRun ?? 0) + _healAmt;
            _vsa._voogooHealsTriggeredThisRun = (_vsa._voogooHealsTriggeredThisRun ?? 0) + 1;
            _vsa._voogooHealAmountThisRun     = (_vsa._voogooHealAmountThisRun ?? 0) + _healAmt;
            trackItemProc('item_voogoo_rager');
            state.screenShakeTimer     = Math.max(state.screenShakeTimer, 0.4);
            state.screenShakeIntensity = Math.max(state.screenShakeIntensity, 16);
            state.pickupNotifications.push({
              text: `VOOGOO RAGER! +${_healAmt} HP`, color: '#00ff88',
              timer: 2.5, initialTimer: 2.5, fontSize: 32, position: 'screen_center',
            });
            playLastStandActivate();
            return;
          }
        }

        // ── Last Stand — one save from death per run ──
        if (projectedHp <= 0 && state.lastStandAvailable) {
          // BETA 14.21 — Last Stand still counts as a hit (HP went down then
          // got clamped to 1% maxHp). Bot CSV will see this in the hit count
          // even though the run continues.
          state._enemyAttacksHitThisRun = (state._enemyAttacksHitThisRun ?? 0) + 1;
          // BETA 16.51 — Last Stand is a BASELINE mechanic (one pity-save
          // per run that EVERY player gets), not an item-based revive.
          // Counting it as a revive event meant PSBOT cycle-30k saw 54/54
          // runs with revives = 1 even when no Sheeesh / Liar / etc. was
          // equipped. Now: Last Stand has its OWN counter
          // (_lastStandTriggersThisRun) that's distinct from the item-based
          // revive counter. Telemetry consumers can read whichever they
          // care about.
          {
            const _sAnyLS = state as unknown as Record<string, number | undefined>;
            _sAnyLS._lastStandTriggersThisRun = (_sAnyLS._lastStandTriggersThisRun ?? 0) + 1;
          }
          state.lastStandAvailable = false;
          // Drop to 1% of max HP (minimum 1)
          p.hp = Math.max(1, Math.round(p.maxHp * 0.01));
          // Long i-frames — give the player 1.5s to escape the situation
          p.invincibleTimer = 1.5;
          // MASSIVE screen shake — far more intense than a regular hit
          state.screenShakeTimer     = 0.80;
          state.screenShakeIntensity = 30;
          // Full-screen red flash + "LAST STAND" text overlay
          state.lastStandFlashTimer  = 1.0;
          // BETA 16.55 — PSBOT cycle-30m. Dylan: "i need a popup says
          // SHEEESH with a heart emoji or something and an audio that it
          // activated. NO AUDIO on Sheeesh / Last Stand / Liar Deceiver."
          // Last Stand was previously SILENT and only had a small "LAST
          // STAND" overlay (8655 in engine.ts). Now: dedicated audio cue +
          // gold screen-center skull popup so the pity-save reads.
          playLastStandActivate();
          state.pickupNotifications.push({
            text: '💀 LAST STAND 💀',
            color: '#ffd700',
            timer: 4.0,
            initialTimer: 4.0,
            fontSize: 42,
            position: 'screen_center',
          });
          playPlayerHit();
          return;
        }

        // BETA 14.21 — outcome attribution. Anything past this point applies
        // HP damage (Last Stand returns above; shield-fully-absorbed returns
        // earlier and is intentionally NOT counted as a hit per spec). This
        // is the "true HP loss" landed-attack count.
        state._enemyAttacksHitThisRun = (state._enemyAttacksHitThisRun ?? 0) + 1;
        p.hp = Math.max(0, projectedHp);
        // BETA 21.27 — MEGABONK CONTACT MODEL (see constants above). No long global
        // i-frame (that made the whole swarm hit like one enemy). Per-enemy cooldowns
        // let each enemy land; a rolling window caps total HP loss so a wall can't
        // one-frame a full-HP player. Hit the window cap → block the rest of the swarm
        // for the remainder of the window (the ~1s "GET OUT" beat).
        if (p.hp > 0) {
          // CO-OP PHASE 2 deep-review fix (2026-07-06, confirmed finding): the
          // rolling window used ONE shared stash on state — with 2+ live views,
          // player A's damage fed the sum that granted player B the window
          // i-frame (and a hit on A could reset B's earned "GET OUT" beat).
          // Same shared-slot-×N class as _playerVx (§2) and setLastDamageSource
          // (§5), both already converted. Now keyed per player on the same
          // run-scoped GameState stash (a fresh run = a fresh GameState = the
          // exact old lifecycle); single-player = one key, byte-identical math.
          const _swinAll = (state as unknown as { _contactDmgWindows?: Record<string, { end: number; sum: number }> });
          const _wins = _swinAll._contactDmgWindows ?? (_swinAll._contactDmgWindows = {});
          const _swin = _wins[playerId] ?? (_wins[playerId] = { end: 0, sum: 0 });
          const _now = state.time ?? 0;
          if (_now >= _swin.end) {
            _swin.end = _now + CONTACT_WINDOW_SEC;
            _swin.sum = 0;
          }
          _swin.sum += reduced;
          // BETA 21.46 — GOD MODE skips the protective window i-frame entirely so
          // the swarm keeps landing every contact (no "GET OUT" beat) → you go down
          // fast. Normal play keeps the ~1s window cap.
          if (!_godActive && _swin.sum >= p.maxHp * CONTACT_WINDOW_CAP_FRAC) {
            p.invincibleTimer = Math.max(p.invincibleTimer, _swin.end - _now);
          } else {
            // short enough that OTHER enemies still land this frame (the swarm pressure)
            p.invincibleTimer = Math.max(p.invincibleTimer, MIN_CONTACT_IFRAME);
          }
        }
        // BETA 20.87 — floating RED damage number on the player so Dylan can SEE
        // how hard each hit lands (his ask: "see how much damage in number an
        // enemy is hitting us"). reduced = post-armor/shield HP actually lost.
        if (reduced > 0) {
          const _wsDmg = getWeaponSystemState();
          if (_wsDmg) {
            spawnDamageNumber(_wsDmg, {
              x: p.x, y: p.y - 18, value: Math.round(reduced),
              color: '#ff4d4d', velocityY: -34, label: `-${Math.round(reduced)}`,
            });
          }
        }
        // Accumulate damage for death screen stats
        if (state.damageTakenThisRun !== undefined) state.damageTakenThisRun += reduced;
        // BETA 14.36 — Phase 5 timing baselines.
        if ((state._firstDamageTakenTime ?? 0) === 0) state._firstDamageTakenTime = state.time;
        // longestStretchBetweenHitsMs: the gap between this hit and the
        // previous hit. Track previous hit time on a state-stashed field.
        const lastHitT = (state as unknown as { _lastDamageHitTime?: number })._lastDamageHitTime ?? 0;
        if (lastHitT > 0) {
          const gapMs = (state.time - lastHitT) * 1000;
          if (gapMs > (state._longestStretchBetweenHitsMs ?? 0)) {
            state._longestStretchBetweenHitsMs = gapMs;
          }
        }
        (state as unknown as { _lastDamageHitTime?: number })._lastDamageHitTime = state.time;
        // Silly Juice: kill stacks reset on taking damage
        resetSillyJuiceOnDamage();
        // BETA 16.91 — Adrenaline Pod (UC03) reactive trigger. Set the
        // 4s timer if player has the item equipped. applyAllBonusesToPlayer
        // reads this each frame and adds +12% atk speed per stack while
        // active. The flat damage path here is the right place — the
        // trigger fires on every contact damage event regardless of
        // armor/shield absorption (Dylan: "rip back at the threats").
        {
          const _itemsForAdrenaline = getItemState();
          const _hasAdrenaline = _itemsForAdrenaline?.inventory.some(s => s.item.id === 'UC03') === true;
          if (_hasAdrenaline) {
            (state as unknown as { _adrenalineReactiveUntil?: number })._adrenalineReactiveUntil = (state.time ?? 0) + tuneOf('UC03', 'durationSeconds', 4); // BETA 21.79 — editable
          }
        }
        // BETA 15.26 — character passive: Sponge Absorb (+1 armor stack /
        // 3s window), Mr Silly's character-passive Silly Juice (reset).
        onPlayerTookDamage(state);
        // BETA 14.04 — Not Xanax (perc N13): set post-damage timer so the
        // condition gate `recently_damaged` evaluates true for 3s. itemsAdapter
        // perc-eval reads state._postDamageTimer; engine.ts ticks it down each
        // frame in the player update.
        // BETA 21.18 — Dylan's "just a nudge" difficulty pass: widen the no-heal
        // window after a hit 3s → 5s. enemyAttacksHit ≈ 1/4.8s in his run, so a 3s
        // pause left regen running most of combat; 5s means a player still being
        // touched ~every 5s gets near-zero passive heal (regen + orbs both gate on
        // this), without touching his locked base regen rate.
        state._postDamageTimer = 5.0;
        // BETA 14.58 — N13 Not Xanax procCount tracker (player took damage,
        // perc activates for 3s).
        // BETA 20.73 — audit fix: was UNCONDITIONAL, crediting N13 procs in
        // every run (123/123 runs showed procs vs 11 actual picks). Gate on
        // ownership via the per-frame N13 snapshot (engine.ts:2668 — 0 when
        // the perc isn't owned), same spirit as the UC03 gate above.
        if ((state._notXanaxAccumulatedSnapshot ?? 0) > 0) {
          trackPercProc('N13');
          // BETA 14.62 — Synergy: notXanax_movementSpeed fires when N13 procs
          // (any MS source equipped — N13 itself grants MS, so always fires
          // when N13 is equipped + procs).
          trackSynergyTrigger('notXanax_movementSpeed', 0);
        }

        // ── Bubble Wrap invuln check ──
        // Must read bubble wrap invuln BEFORE we possibly set p.invincibleTimer below
        const hadBubbleWrapInvuln = getBubbleWrapInvulnTimer() > 0;

        // BETA 14.29 — REMOVED `p.invincibleTimer = iFrameGrant;` post-contact
        // iframe set. With this in place, only ONE enemy could hit per
        // iFrameGrant interval (e.g. 0.5s for heavies) regardless of how many
        // were stacked on the player. User: "i have 4 [bosses] on top of me i
        // am standing still. takes like 10 hits..." Now every enemy hits at
        // its own per-enemy contactCooldown (0.5s zombie / 0.7s timmy / 0.8s
        // heavies), so 4 bosses piled up = 4 hits per ~0.8s = real swarm
        // damage. Single-enemy combat is unchanged (1 hit per 0.5–0.8s).
        // Bubble Wrap, Last Stand, lab respawn, and shield-fully-absorbed
        // iframe-sets are all preserved — those are SAVES, not normal hits.
        void iFrameGrant;

        // ── Item: Bubble Wrap (R06) — reflect 30% + 10% invuln chance ──
        if (!hadBubbleWrapInvuln) {
          const items = getItemState();
          const bubbleSlot = items?.inventory.find(s => s.item.id === 'R06');
          if (bubbleSlot) {
            spawnBubbleWrapReflect(p.x, p.y, reduced, bubbleSlot.stacks);
            if (rollRng() < tuneOf('R06', 'invulnChance', 0.10)) { // BETA 21.81 — editable invuln proc chance
              const _bubbleInvuln = tuneOf('R06', 'bubbleInvulnSeconds', 1.0);
              triggerBubbleWrapInvuln(_bubbleInvuln);
              p.invincibleTimer = Math.max(p.invincibleTimer, _bubbleInvuln);
            }
          }
        }

        // ── Item: Diarrhea Gas (R11) — spawn poison cloud on taking damage ──
        // ── Item: Morning Wood (R10) — 25% chance for thorns AoE when hit ──
        {
          const items = getItemState();
          const gasSlot = items?.inventory.find(s => s.item.id === 'R11');
          if (gasSlot) {
            // BETA 14.65 — pass damage + area multipliers so R11 scales with
            // player damageBonus + attackAreaBonus (was hardcoded dps × stacks).
            const _gasDmgMult = 1 + ((p.damageBonus ?? 0) / 100);
            const _gasAreaMult = 1 + ((p.attackAreaBonus ?? 0) / 100);
            // BETA 16.30 — cycle-30e Phase 2: read dimension picks for
            // R11 (gasCloudRadius / gasCloudDps / gasCloudLifetime).
            const _gasDims = gasSlot.pickedDimensions ?? {};
            spawnPoisonCloud(p.x, p.y, gasSlot.stacks, _gasDmgMult, _gasAreaMult, {
              radiusBonus:   (_gasDims.gasCloudRadius   ?? 0) * 15,
              dpsBonus:      (_gasDims.gasCloudDps      ?? 0) * 4,
              lifetimeBonus: (_gasDims.gasCloudLifetime ?? 0) * 0.5,
            });
            playDiarrheaGas();  // fart.wav + gas.wav together
            // BETA 14.58 — R11 Diarrhea Gas procCount tracker
            trackItemProc('R11');
            // BETA 16.28 — PSBOT cycle-30f Phase 7 Bug 2 FIX. R11 was firing
            // (trackItemProc landed) but not appearing in groundEffectSources
            // telemetry because spawnPoisonCloud lives in itemEffectSystem
            // which intentionally has no imports from weaponLab. Adding the
            // floor-effect spawn tracker call here so PSBOT's groundEffect
            // Sources field gets `R11:N` entries — confirms the cloud
            // visualization fired in audit data.
            trackFloorEffectSpawn('R11', 3.0);
          }
          const thornPct = p.thorns ?? 0;
          if (thornPct > 0) {
            const woodSlot = items?.inventory.find(s => s.item.id === 'R10');
            if (woodSlot && rollRng() < tuneOf('R10', 'explodeChance', 0.25)) { // BETA 21.81 — editable explode chance
              const thornDmg = reduced * (thornPct / 100);
              spawnMorningWoodExplosion(p.x, p.y, thornDmg, woodSlot.stacks);
              playMorningWoodThorns(p.x, p.y);   // BETA — thorns burst sound (was silent)
            }
            // BETA 20.73 — R03 Wyfe Beater gets its OWN retaliation visual
            // (cast-iron spikes + red-hot tips — VISUAL ONLY, damage flows
            // through the thorns stat exactly as before). It used to borrow
            // R10's brown look, the exact sameness the VFX law bans. Same
            // 25% throttle as R10 so hit-spam can't strobe it.
            const wyfeSlot = items?.inventory.find(s => s.item.id === 'R03');
            if (wyfeSlot && rollRng() < 0.25) { // BETA 20.94 — seeded Wyfe Beater proc
              spawnWyfeBeaterThorns(p.x, p.y);
            }
          }
        }

        playPlayerHit();
        playEnemyHit(p.x, p.y);
        // Screen shake — intensity scales with damage taken.
        // BETA 16.39 — Dylan: "screen shake each time i get hit" — bumped
        // baseline + cap (was 3+reduced*0.08 capped at 8 → 6+reduced*0.18
        // capped at 22) so EVERY hit registers as a meaningful jolt.
        state.screenShakeTimer     = 0.25;
        state.screenShakeIntensity = Math.max(state.screenShakeIntensity, Math.min(22, 6 + reduced * 0.18));
        // BETA 16.39 — Player hit flash timer set on every damage-landing hit.
        // BETA 16.42 — Dylan replaced the red overlay with a floating
        // "💥 -N" damage marker; capture the actual HP-lost amount + a
        // shield flag so the renderer can draw "💥 -N ◈" when shield
        // absorbed it.
        const sAnyFlash = state as unknown as Record<string, number | undefined>;
        sAnyFlash._playerHitFlashTimer = 0.55; // longer to give the float time to rise
        sAnyFlash._playerHitLastDamage = reduced;
        sAnyFlash._playerHitLastWasShield = 0;
      } else {
        // BETA 14.21 — outcome attribution. Iframe blocked the attack entirely
        // (player.invincibleTimer > 0 from a prior hit / Last Stand / Sheeesh
        // / Bubble Wrap / debug god mode etc.). No HP loss, no defensive proc
        // even fired — just denied at the gate.
        state._enemyAttacksMissedByIframe = (state._enemyAttacksMissedByIframe ?? 0) + 1;
      }
  };
  return {
    playerId,
    x: p.x,
    y: p.y,
    // NOTE (part B): facingAngle is the LOCAL player's weapon-facing — the
    // weapon system's per-player conversion is a Phase-2 sibling task. Spawn
    // front-bias only ever consumed the primary view, so SP is unchanged.
    facingAngle: getLastFacingAngle(),
    // BETA 14.71 — BUG-2: was p.size * 0.7. Removed the 0.7 multiplier so
    // player hitbox matches the visible sprite radius. Combined with
    // engine.ts player.size = 32 (was 14), player collision now lines up
    // with the 64px sprite (half = 32 px hitbox).
    hitboxRadius: p.size,
    currentHP: p.hp,
    maxHP: p.maxHp,
    // CO-OP PHASE 2 part A — the store bracket (design §1): per-player
    // systems the reducer calls (getItemState, resetSillyJuiceOnDamage,
    // onPlayerTookDamage, trackers) resolve against THIS view's player;
    // the machine's local player is restored on every exit path.
    damage: (amount: number, iFrameGrant: number, source?: string) => {
      setActiveItemsAdapterPlayer(playerId);
      setActiveCharacterPassivePlayer(playerId);
      try {
        damageImpl(amount, iFrameGrant, source);
      } finally {
        setActiveItemsAdapterPlayer(state.localPlayerId);
        setActiveCharacterPassivePlayer(state.localPlayerId);
      }
    },
    knockbackResist: 0.3,
    thorns: p.thorns ?? 0,
    // BETA 22.02 — LIVE getter/setter so the enemy system's contact-knockback + Timmy-slow
    // writes pass straight through to the REAL state.player. Before this, enemySystem wrote
    // knockbackX/knockbackY/slowUntil onto this throwaway adapter object, which was discarded
    // when updateEnemies returned — so the player movement code (which reads state.player.
    // knockbackX) never saw the shove. THAT is why no contactKnockback value ever knocked you
    // back, no matter how high. Now reads + writes go to `p` (= state.player), so it accumulates
    // across frames and the movement applier flings you. (Same pattern as iFrameTimer/level.)
    get knockbackX() { return (p as { knockbackX?: number }).knockbackX ?? 0; },
    set knockbackX(v: number) { (p as { knockbackX?: number }).knockbackX = v; },
    get knockbackY() { return (p as { knockbackY?: number }).knockbackY ?? 0; },
    set knockbackY(v: number) { (p as { knockbackY?: number }).knockbackY = v; },
    get slowUntil() { return (p as { slowUntil?: number }).slowUntil ?? 0; },
    set slowUntil(v: number) { (p as { slowUntil?: number }).slowUntil = v; },
    // LIVE getter — reads the current value every time enemySystem checks it
    get iFrameTimer() {
      return p.invincibleTimer;
    },
    // BETA 19.51 — Megabonk-style curve balance: pipe the player's current
    // level so waveSystem can gate enemy spawn cap on player power.
    // Under-leveled = fewer enemies (room to grow); over-leveled = more
    // enemies (level-matched pressure).
    get level() {
      return p.level ?? 1;
    },
  };
}

function toCameraForEnemy(state: GameState): CameraForEnemy {
  return {
    x: state.camera.x,
    y: state.camera.y,
    viewportWidth: canvasWidth,
    viewportHeight: canvasHeight,
    zoom: state.camera.zoom,
  };
}

/** Convert EnemyInstance → the simpler Enemy interface the engine expects */
function toEngineEnemy(ei: EnemyInstance): Enemy {
  return {
    id: ei.id,
    type: ei.definitionId,
    // BETA 20.73 — audit fix: variant was never copied, so death attribution
    // (enemyAdapter killedByVariant) and the bot's deathCause logged every
    // boss/elite kill as ':normal'. The 3:00 "zombie/timmy" deaths in the
    // 123-run audit were partly bosses wearing a normal tag.
    variant: ei.variant,
    x: ei.x,
    y: ei.y,
    hp: ei.currentHP,
    maxHp: ei.maxHP,
    speed: ei.speed,
    size: ei.hitboxRadius,
    damage: ei.damage,
    alive: ei.alive,
  };
}

// ─── Public: set canvas dimensions (called from GameCanvas) ─────────
export function setCanvasDimensions(w: number, h: number) {
  canvasWidth = w;
  canvasHeight = h;
}

// ─── Public: get raw state for debug / external access ──────────────
export function getEnemySystemState(): EnemySystemState | null { return enemyState; }
export function getWaveSystemState(): WaveSystemState | null { return waveState; }

// (BETA 23.6x §6 — dead exports `getWaveTimeDisplay` / `getWaveTimeRemaining`
// deleted: zero callers anywhere in client/src; engine reads the floor timer
// through getWaveState() directly.)
export function getCurrentFloor(): number {
  return waveState ? waveState.currentFloor + 1 : 1;
}
export function getEnemyCount(): number {
  return enemyState ? getAliveCount(enemyState) : 0;
}

// ─── The Adapter Module ─────────────────────────────────────────────
export const enemyAdapterModule: EnemiesModule = {
  init(_state: GameState) {
    enemyState = createEnemySystemState();
    waveState = createWaveSystemState();
    clearEnemyProjectiles();
    lastCleanupTime = 0;
    // BETA 23.6x — §6 init-gap fix: between init and frame 1 of a NEW run,
    // getEnemies() could serve the PREVIOUS run's alive array and
    // lastGameState briefly pointed at the dead run. Reset all three.
    _aliveEnemiesCache = [];
    _aliveEnemiesCacheFrame = -1;
    lastGameState = null;
    // CO-OP part B — per-player velocity tracker is run-scoped world state.
    resetPlayerVelocityTracker();
  },

  update(state: GameState, dt: number) {
    if (!enemyState || !waveState) return;
    lastGameState = state;
    // BETA 20.01 — frame counter for getEnemies cache invalidation (see below).
    _frameCounter++;

    // CO-OP PHASE 2 part A — one live view per roster player (SP: just p1),
    // built once per frame; enemy targeting must see all players at once
    // (Bram ask b: iterate the roster, never assume a singular state.player).
    const playerIds = Object.keys(state.players) as PlayerId[];
    const players = playerIds.map(id => toPlayerForEnemy(state, id));
    // BETA 23.6x — CO-OP PHASE 2 part C (§3.3): sync the machine-local player id
    // into the itemEffectSystem leaf so its AoE damage requests are owner-stamped
    // at enqueue. SP: always 'p1' (the leaf's own default) — a no-op.
    setItemEffectLocalPlayerId(state.localPlayerId);
    const camera = toCameraForEnemy(state);
    // Use state.time (accumulated dt, paused during non-playing phases) instead of
    // wall-clock time. This prevents the wave system from advancing while the game
    // is paused — which caused difficulty/floor-timer to jump forward on resume.
    const runTime = state.time;
    // BETA 19.98 — push currentFloor to enemySystem so getScaledStats applies
    // the floor multiplier (floor 1 = 1×, floor 2 = 2×, etc.) per Dylan's
    // "2 times harder" spec.
    setCurrentFloorForScaling(waveState.currentFloor);
    // BETA 20.77 — clock fix: push the floor start so the endless overdrive
    // runs floor-local while the base ramp stays monotonic.
    setFloorStartForScaling(waveState.floorStartTime);
    // BETA 21.31 — endless CC resistance ramps on the GLOBAL clock so the swarm can
    // close in late-game: 0 before 10:00, climbing to 0.7 by 16:00.
    setEndlessCcResist((runTime ?? 0) < 600 ? 0 : Math.min(0.7, ((runTime ?? 0) - 600) / 360));

    // 1. Update wave system (handles spawning)
    const wallChecker = (x: number, y: number, size: number) => !canMoveTo(state, x, y, size, true);
    const waveResult = updateWaveSystem(
      waveState,
      enemyState,
      dt,
      runTime,
      players,
      camera,
      wallChecker,
    );

    // Sync wave/floor number back to GameState so onRunEnd can report it correctly
    state.wave = waveState.currentFloor + 1;

    // Handle wave events
    if (waveResult.bossSpawnRequested) {
      console.log(`[Wave] Boss spawn requested: index ${waveResult.bossIndex}`);
    }
    if (waveResult.floorComplete) {
      console.log(`[Wave] Floor ${waveState.currentFloor + 1} complete!`);
      // BETA 13.37: light up the boss portal so the player can walk in for the final fight.
      // Without this, the portal was rendered but never activatable — final boss unreachable.
      if (state.portal) {
        state.portal.active = true;
        state.pickupNotifications?.push({
          text: '⚠ PORTAL ACTIVATED — FIND IT', color: '#ff6600', timer: 4.0,
        });
      }
    }
    if (waveResult.hordeStarted && waveResult.hordeName && state.runUnlockNotifs) {
      state.runUnlockNotifs.push({
        label: `⚠ ${waveResult.hordeName}`,
        type: 'horde',
        timer: 3.5,
        slideProgress: 0,
      });
    }
    // BETA 20.71 — telegraph the recurring swarms (1/3/5/7 min). They were
    // firing SILENTLY — only HORDE_SCHEDULE events were announced — so the
    // 3:00 density spike felt like an ambush ("die just because im 3 min in").
    // Megabonk/VS rule: hordes must be telegraphed + escapable, never a
    // surprise wall. The 30s swarm ramps, so this gives real reaction time.
    if (waveResult.swarmStarted && state.runUnlockNotifs) {
      state.runUnlockNotifs.push({
        label: '⚠ HORDE INCOMING',
        type: 'horde',
        timer: 3.5,
        slideProgress: 0,
      });
    }

    // 2. Update all enemy instances (movement, AI, touch damage)
    const lootDrops = updateEnemies(
      enemyState,
      dt,
      runTime,
      players,
      camera,
      wallChecker,
      state.map?.roomMap?.rooms,
      state, // BETA 13.25: gameState for getGreenZoneRedirect lookup
    );

    // 3. Handle loot drops from touch-damage kills — spawn as world loot orbs
    // BETA 23.6x — CO-OP PHASE 2 part C (§3.4): drops stamped with a killer id
    // (thorn-reflect kills) scatter at the KILLER's feet; unstamped drops
    // (enemy-eats-enemy devour, legacy) keep the old local-player anchor. The
    // batching walks the array IN ORDER and only splits when the anchor VALUE
    // changes — in SP every stamp is p1 and p1's view aliases state.player, so
    // every anchor resolves to state.player.x/y and this stays ONE spawnDrops
    // call with the exact original drop order: byte-identical to the old line.
    // BETA 23.6x — CO-OP PHASE 2 part D: no trailing owner here on purpose — a
    // batch can MIX stamped (thorn-kill) and unstamped (devour/legacy) drops,
    // so ownership rides the per-drop killerPlayerId stamps, which spawnDrops
    // now reads onto each orb (unstamped = shared orb, unchanged).
    if (lootDrops.length > 0 && state.lootOrbs) {
      const _anchorOf = (d: (typeof lootDrops)[number]): { x: number; y: number } => {
        const kp = d.killerPlayerId ? players.find(p => p.playerId === d.killerPlayerId) : undefined;
        return kp ? { x: kp.x, y: kp.y } : { x: state.player.x, y: state.player.y };
      };
      let _batchStart = 0;
      let _a = _anchorOf(lootDrops[0]);
      for (let _i = 1; _i < lootDrops.length; _i++) {
        const _b = _anchorOf(lootDrops[_i]);
        if (_b.x !== _a.x || _b.y !== _a.y) {
          spawnDrops(state.lootOrbs, lootDrops.slice(_batchStart, _i), _a.x, _a.y);
          _batchStart = _i;
          _a = _b;
        }
      }
      spawnDrops(state.lootOrbs, lootDrops.slice(_batchStart), _a.x, _a.y);
    }

    // 4. Track kill count
    const _prevKillCount = state.killCount ?? 0;
    state.killCount = enemyState.totalKills;
    // BETA 20.40 — surface elite+boss kills to the live payload (deep-audit gap:
    // only the MEGA boss was counted; regular boss/elite kills had no row).
    // totalEliteKills already counts both 'elite' and 'boss' variants (enemySystem.ts:2184/2187).
    (state as unknown as Record<string, number>)._bossEliteKillsThisRun = enemyState.totalEliteKills;
    // BETA 20.58 — track enemies removed WITHOUT dying (the "vanishing elite" bug tracker).
    (state as unknown as Record<string, number>)._enemyDespawnsNoDeathThisRun = enemyState.enemyDespawnsNoDeath ?? 0;
    // BETA 14.36 — Phase 5 timing baseline. firstBloodTime captures state.time
    // when killCount first becomes >= 1.
    if (_prevKillCount === 0 && (state.killCount ?? 0) >= 1 && (state._firstBloodTime ?? 0) === 0) {
      state._firstBloodTime = state.time;
    }

    // 5. Periodic cleanup
    if (runTime - lastCleanupTime >= 2.0) {
      cleanupDead(enemyState);
      lastCleanupTime = runTime;
    }

    // BETA 16.31 — Enemy throwable speed ramp.
    // Dylan: "im 4 minutes left in the game. at that moment all enemie's
    // throwables should be moving twice the speed they are. and gradually
    // get faster over time."
    // Curve: 1.0× until floor-time-remaining drops to 240s (4:00), then
    // ramps linearly to 3.0× by 0:00. Final swarm continues to ramp +1×
    // over its first 2 minutes, capping at 4.0×. Curve is per-floor so
    // every floor has a "danger zone" final 4 minutes (matches Dylan's
    // intent of escalating pressure as time drains).
    {
      const remaining = getFloorTimeRemaining(waveState);
      let mult = 1.0;
      if (waveState.inFinalSwarm) {
        const swarmExtra = Math.min(1.0, (waveState.finalSwarmTime ?? 0) / 120);
        mult = 3.0 + swarmExtra; // 3.0× → 4.0× over 2 min of final swarm
      } else if (remaining <= 240) {
        const t = (240 - remaining) / 240; // 0 at 240s left → 1 at 0s left
        mult = 2.0 + Math.max(0, Math.min(1, t)); // 2.0× → 3.0×
      }
      setEnemyProjectileSpeedMultiplier(mult);
    }

    // 6a. Apply AoE damage requests from itemEffectSystem (poison clouds, burst explosions)
    for (const req of drainAoeDamageRequests()) {
      const r2 = req.radius * req.radius;
      const isGas = isGasSource(req.sourceItemId); // BETA 16.22
      // BETA 23.6x — CO-OP PHASE 2 part C (§3.3): thread the request's owner into
      // the damage call so kill credit is never lost on the way in (ledger = Bram
      // E4+). Requests are enqueue-stamped with the machine-local player; the
      // fallback covers stale/unstamped requests — same id in SP either way.
      const _reqOwner = req.ownerPlayerId ?? state.localPlayerId;
      for (const enemy of enemyState.enemies) {
        if (!enemy.alive) continue;
        const dx = enemy.x - req.x;
        const dy = enemy.y - req.y;
        if (dx * dx + dy * dy <= r2) {
          // BETA 22.03 — capture + spawn the kill's loot (was discarded → AoE finishers
          // dropped no gold/xp: poison clouds R11, Anus Rocket R08, Nutty T-Bag R16, NickMerc
          // R17, R09 bursts). Use enemy.x/y now (still valid before cleanupDead splices it).
          const _aoeRes = damageEnemyFn(enemyState, enemy.id, req.damage, 0, 0, 1.0, req.sourceItemId,
            { sourcePlayerId: _reqOwner }); // BETA 23.6x — CO-OP PHASE 2 part C (§3.3) attribution thread
          if (_aoeRes?.killed && state.lootOrbs) {
            // BETA 23.6x — CO-OP PHASE 2 part C (§3.3): the ≥1-XP fallback drop
            // carries the owner too (rolled drops were stamped inside damageEnemy).
            // Scatter position stays the ENEMY's body — unchanged from 22.03.
            const _aoeDrops = _aoeRes.drops.length ? _aoeRes.drops
              : [{ type: 'xp' as const, chance: 1, amount: 1, killerPlayerId: _reqOwner }];
            // BETA 23.6x — CO-OP PHASE 2 part D: thread the owner into the orb
            // spawn (trailing param — positionals untouched). Per-drop stamps
            // (rolled drops) win inside spawnDrops; the tail covers the rest.
            spawnDrops(state.lootOrbs, _aoeDrops, enemy.x, enemy.y, _reqOwner);
          }
          // BETA 14.58 — accumulate item dmgDealt by sourceItemId.
          // Covers R08 Anus Rocket, R10 Morning Wood, R11 Diarrhea Gas,
          // R16 Nutty T-Bag, R17 NickMerc Jerk, item_big_mac burn etc.
          if (req.sourceItemId) trackItemDmg(req.sourceItemId, req.damage);
          // BETA 16.22 — Per Dylan: gas damage triggers per-enemy gas
          // cloud overlay + floating tick-damage text. Fires for every
          // gas-tagged AoE tick that lands on a living enemy.
          if (isGas) {
            notifyEnemyGasTick(enemy.id, enemy.x, enemy.y, req.damage);
          } else if (req.damage > 0) {
            // BETA 16.31 — Dylan: "there is a item that turns the xp or
            // gold orbs into attacks. if an enemy walks into gold or xp
            // they get damaged. there is not on screen on enemy emoji
            // or numbers showing this attack is happening in real time
            // on enemies." Root cause: every non-gas AoE source (R09
            // Peanut's Big Brain bursts, R08 rocket, R10 wood, R16 nuts,
            // R17 jerky, banana, whoopie) damaged enemies silently —
            // no floating number, no visual confirmation. Now: any
            // non-gas AoE hit spawns a floating damage number on the
            // enemy that took it. Gas keeps its dedicated tick overlay.
            const ws = getWeaponSystemState();
            if (ws) {
              spawnDamageNumber(ws, {
                x: enemy.x + (Math.random() - 0.5) * 14,
                y: enemy.y - 14,
                value: Math.round(req.damage),
                isCrit: false,
                timer: 0.45,
                velocityY: -50,
              });
            }
          }
        }
      }
    }

    // 6. Check game over — DELEGATED TO ENGINE.TS
    // BETA 16.02 — Per Dylan: "death screen still broken. main game music
    // still playing. WHAT THE FUCK is going on???" Root cause: this
    // adapter was setting state.phase = 'gameover' directly, BEFORE
    // engine.ts's own player.hp <= 0 check (line ~1921) had a chance to
    // run. By the time engine.ts checked, phase was already 'gameover'
    // and the rich death handler (Sheeesh / Liar / Lab respawn / audio
    // swap / eulogy / telemetry) was completely SKIPPED. Result: stars
    // rendered (because renderDeathScreen runs every frame on phase=
    // 'gameover'), but no music change, no eulogy, no RIP/grade/buttons
    // (those need ctx state from the rich handler).
    //
    // Fix: don't set phase here. Just let player.hp drop to 0 — engine.ts
    // detects it next tick and runs the full death handler with audio +
    // visuals + revive logic.
    // (Original line: if (state.player.hp <= 0 && state.phase === 'playing') state.phase = 'gameover';)
  },

  render(ctx: CanvasRenderingContext2D, state: GameState, _viewBounds: ViewBounds) {
    if (!enemyState) return;
    const camera = toCameraForEnemy(state);
    renderEnemies(enemyState, ctx, camera);
  },

  getEnemies(): Enemy[] {
    if (!enemyState) return [];
    // BETA 20.01 — Per-frame cache. getEnemies() is called 6× per frame
    // (engine.ts:2990, 3071, 3512, 3649, 3819, 8451) and each call was doing
    // getAliveEnemies().map(toEngineEnemy) = 1 filter + N object allocations.
    // At 200 enemies × 6 calls = 1200 allocs/frame = 72k allocs/sec just for
    // this. Now: compute once per frame, return same array reference for the
    // other 5 calls. Cache invalidated by _aliveEnemiesCacheFrame counter
    // (bumped at the start of update() each frame).
    if (_aliveEnemiesCacheFrame === _frameCounter) return _aliveEnemiesCache;
    _aliveEnemiesCache = getAliveEnemies(enemyState).map(toEngineEnemy);
    _aliveEnemiesCacheFrame = _frameCounter;
    return _aliveEnemiesCache;
  },

  // BETA 23.6x — CO-OP PHASE 2 part C (§3.2): optional trailing sourcePlayerId =
  // the attacking player. Every engine call site (4737/4843/5517/5610/5751/5855/
  // 6089) stays UNCHANGED and defaults to the machine-local player (SP: p1 —
  // byte-identical); the engine's per-player item loops pass the real id when
  // they convert. Bram signature courtesy: trailing + optional, positional args
  // never reorder.
  damageEnemy(enemyId: number, damage: number, knockback?: { x: number; y: number }, sourcePlayerId?: PlayerId) {
    if (!enemyState) return;
    const angle = knockback ? Math.atan2(knockback.y, knockback.x) : 0;
    const force = knockback ? Math.sqrt(knockback.x ** 2 + knockback.y ** 2) : 0;
    const _srcPid: string | undefined = sourcePlayerId ?? lastGameState?.localPlayerId;
    // BETA 22.03 — Dylan: "some enemy deaths drop no gold or xp, especially crowned ones."
    // ROOT CAUSE: this wrapper is the kill path for EVERY special/item finisher (Rage Quit
    // map-wipe, Whoopie Cushion, Asmon's Banana, Bungulators, Iranian Bird, R09 Big Brain
    // gold/xp-orb attack). It used to call damageEnemyFn and THROW AWAY the returned drops, so
    // those kills spawned nothing. Elites felt it worst — biggest loot + most often the tanky
    // target finished by these high-damage AoE/ultra sources. Now: capture the enemy's position
    // BEFORE the kill (it's flagged dead during) and spawn its loot (≥1 XP guaranteed), mirroring
    // the weapon kill path (weaponAdapter.ts:514-521). The weapon path stays separate, so no
    // double-spawn.
    const _e = enemyState.enemies.find(e => e.id === enemyId);
    const _ex = _e?.x ?? 0, _ey = _e?.y ?? 0;
    const result = damageEnemyFn(
      enemyState,
      enemyId,
      damage,
      force,
      angle,
      1.0, // neutral luck multiplier
      undefined, // no sourceItemId — wrapper callers never pass one today
      { sourcePlayerId: _srcPid }, // BETA 23.6x — CO-OP PHASE 2 part C (§3.2) attribution thread
    );
    if (result?.killed && lastGameState?.lootOrbs) {
      // BETA 23.6x — CO-OP PHASE 2 part C (§3.2): this drop-spawn is the wrapper's
      // attribution CHOKE POINT — rolled drops come back killer-stamped from
      // damageEnemy, and the ≥1-XP fallback is stamped here, so the id survives.
      // The scatter POSITION is the dead enemy's body (22.03), which doesn't
      // depend on the attacker — nothing further to thread positionally.
      // BETA 23.6x — CO-OP PHASE 2 part D: owned-orbs rail 1 — killerPlayerId
      // now flows INTO the orb spawn (trailing owner param + per-drop stamps
      // read inside spawnDrops). Orbs remember their earner; absorb rules /
      // colors / bonuses are later parts.
      const drops = result.drops.length ? result.drops
        : [{ type: 'xp' as const, chance: 1, amount: 1, killerPlayerId: _srcPid }];
      spawnDrops(lastGameState.lootOrbs, drops, _ex, _ey, _srcPid);
    }
  },

  spawnBossAt(definitionId: string, x: number, y: number): number | null {
    if (!enemyState || !lastGameState) return null;
    // §5 hygiene — spawn positioning is machine-local dev/shrine tooling:
    // explicit local player view, never an implicit singular.
    const player = toPlayerForEnemy(lastGameState, lastGameState.localPlayerId);
    const camera = toCameraForEnemy(lastGameState);
    const wallChecker = (wx: number, wy: number, r: number) =>
      !canMoveTo(lastGameState!, wx, wy, r, true);
    const instance = spawnEnemy(
      enemyState, definitionId, 'boss',
      player, camera,
      lastGameState.time ?? 0,
      wallChecker,
      true,       // ignoreMaxCount — shrine boss always spawns
      { x, y },   // atPos — right at the shrine
    );
    return instance?.id ?? null;
  },

  isEnemyAlive(instanceId: number): boolean {
    if (!enemyState) return false;
    return enemyState.enemies.some(e => e.id === instanceId && e.alive);
  },

  getWaveState() {
    return waveState;
  },

  // BETA 13.70 FPS: O(1) alive count — autoPlaytester_v2 was running 5 filters per frame.
  aliveCount() {
    return enemyState?.aliveCount ?? 0;
  },
};

// ─── Dev console helpers ──────────────────────────────────────────────────────

/** Spawn any enemy type at a specific world position (dev panel use only) */
export function debugSpawnEnemy(definitionId: string, x: number, y: number): void {
  if (!enemyState || !lastGameState) return;
  const player     = toPlayerForEnemy(lastGameState, lastGameState.localPlayerId);
  const camera     = toCameraForEnemy(lastGameState);
  const wallChecker = (wx: number, wy: number, r: number) =>
    !canMoveTo(lastGameState!, wx, wy, r, true);
  spawnEnemy(enemyState, definitionId, 'normal', player, camera,
    lastGameState.time ?? 0, wallChecker, true, { x, y });
}

/** Kill every alive enemy instantly (dev panel use only) */
export function debugKillAll(): void {
  if (!enemyState) return;
  for (const e of enemyState.enemies) {
    if (e.alive) e.alive = false;
  }
}

/**
 * Spawn the final boss fight (legacy doc — see 23.39 tombstone below).
 * Returns the unique IDs of the spawned enemies so engine can track them for the win condition.
 */
// ─── BETA 23.02 · TEST DUMMIES (map-building mode) ──────────────────────────
// Dylan: "give you test dummies 4 weapons that shoot." With the enemy UPDATE
// gated off (MAP_BUILD_NO_ENEMIES), spawned enemies render but never move or
// attack — perfect stationary targets. This keeps 4 zombie dummies standing
// in a ring around the spawn point so weapons always have something to shoot.
let _lastDummyCheck = 0;
// NOTE: takes the live GameState directly — lastGameState is only refreshed
// by the enemy UPDATE tick, which is exactly what map-build mode gates off.
export function maintainTestDummies(gs: GameState, cx: number, cy: number, nowSec: number): void {
  if (!enemyState) return;
  if (nowSec - _lastDummyCheck < 2) return;   // throttle: check every 2s
  _lastDummyCheck = nowSec;
  let alive = 0;
  for (const e of enemyState.enemies) if (e.alive) alive++;
  if (alive >= 4) return;
  const player = toPlayerForEnemy(gs, gs.localPlayerId);
  const camera = toCameraForEnemy(gs);
  const R = 380;
  const offs = [[0, -R], [R, 0], [0, R], [-R, 0]];
  for (let i = alive; i < 4; i++) {
    const o = offs[i % 4];
    spawnEnemy(
      enemyState, 'zombie', 'normal', player, camera,
      0 /* runTime 0 = base stats */, undefined, true,
      { x: cx + o[0], y: cy + o[1] },
    );
  }
}

export function spawnFinalBoss(_portalX: number, _portalY: number): number[] {
  // 23.39 — TOMBSTONE. The final boss (CaseOh mega) is fully deleted; the
  // portal charges without a fight until the Phase C roster ships its own
  // final boss. Kept exported so engine + PSBOT-owned files compile.
  return [];
}

export function getDebugEnemyNav(): Array<{ x: number; y: number; wpX: number; wpY: number }> {
  if (!enemyState) return [];
  return enemyState.enemies
    .filter(e => e.alive)
    .map(e => ({ x: e.x, y: e.y, wpX: e.navWaypointX, wpY: e.navWaypointY }));
}
